/**
 * Wallet-device session protocol client.  It only submits public JWKs and
 * signatures; private CryptoKeys, vault keys, and cached tokens are never
 * logged or attached to unrelated requests.
 */
import { getOrchestratorBaseUrl } from '../magic-link/guestSession';
import { normalizeChatWallet, signDeviceChallenge } from './chatDeviceIdentity';
const TOKEN_PREFIX = 'dwdata_wallet_device_session:';
const memoryCache = new Map();
function canonicalOrigin(origin = window.location.origin) {
    const value = new URL(origin).origin;
    if (value !== origin.replace(/\/$/, ''))
        throw new Error('Device origin must not include a path, query, or fragment');
    return value;
}
function assertPurpose(purpose) {
    if (purpose !== 'chat' && purpose !== 'admin_presence')
        throw new Error('Unsupported wallet device purpose');
}
function checkedWallet(wallet) {
    const normalized = normalizeChatWallet(wallet);
    if (!/^0x[a-f0-9]{40}$/.test(normalized))
        throw new Error('Invalid wallet address');
    return normalized;
}
function publicP256Jwk(jwk) {
    if (jwk.kty !== 'EC' ||
        jwk.crv !== 'P-256' ||
        typeof jwk.x !== 'string' ||
        typeof jwk.y !== 'string' ||
        jwk.d !== undefined) {
        throw new Error('Device signing public key is not a P-256 public JWK');
    }
    return { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y };
}
function cacheKey(wallet, deviceId, purpose, origin) {
    return `${checkedWallet(wallet)}|${deviceId}|${purpose}|${origin}`;
}
function storageKey(key) {
    return TOKEN_PREFIX + key;
}
function loadCached(key) {
    const memory = memoryCache.get(key);
    if (memory && memory.expiresAt > Date.now())
        return memory;
    memoryCache.delete(key);
    try {
        const parsed = JSON.parse(sessionStorage.getItem(storageKey(key)) || 'null');
        if (!parsed || parsed.expiresAt <= Date.now()) {
            sessionStorage.removeItem(storageKey(key));
            return null;
        }
        memoryCache.set(key, parsed);
        return parsed;
    }
    catch {
        return null;
    }
}
function saveCached(key, session) {
    memoryCache.set(key, session);
    try {
        sessionStorage.setItem(storageKey(key), JSON.stringify(session));
    }
    catch {
        // A tab-local in-memory cache is sufficient when storage is blocked.
    }
}
async function requestJson(path, init) {
    const response = await fetch(`${getOrchestratorBaseUrl()}/wallet-devices${path}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(typeof body.error === 'string' ? body.error : `HTTP ${response.status}`);
        error.code = body.error;
        throw error;
    }
    return body;
}
function challengeBody(kind, walletAddress, identity, purpose, origin) {
    return {
        kind,
        walletAddress: checkedWallet(walletAddress),
        deviceId: identity.deviceId,
        purpose,
        origin,
        ...(kind === 'registration' ? { publicKey: publicP256Jwk(identity.signingPublicJwk) } : {}),
    };
}
export function buildWalletDeviceRegistrationMessage(input) {
    const key = publicP256Jwk(input.publicKey);
    return [
        'Dwinity wallet device registration',
        `challenge: ${input.challenge}`,
        `wallet: ${checkedWallet(input.walletAddress)}`,
        `device: ${input.deviceId}`,
        `publicKey: ${JSON.stringify(key)}`,
        `purpose: ${input.purpose}`,
        `origin: ${canonicalOrigin(input.origin)}`,
    ].join('\n');
}
export function buildWalletDeviceSessionMessage(input) {
    return [
        'Dwinity wallet device session',
        `challenge: ${input.challenge}`,
        `wallet: ${checkedWallet(input.walletAddress)}`,
        `device: ${input.deviceId}`,
        `purpose: ${input.purpose}`,
        `origin: ${canonicalOrigin(input.origin)}`,
    ].join('\n');
}
function signatureBase64Url(bytes) {
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export async function registerWalletDevice(walletAddress, identity, signWalletMessage, purpose = 'chat', origin = window.location.origin) {
    assertPurpose(purpose);
    const canonical = canonicalOrigin(origin);
    if (identity.wallet !== checkedWallet(walletAddress))
        throw new Error('Device identity wallet mismatch');
    const body = challengeBody('registration', walletAddress, identity, purpose, canonical);
    const { challenge } = await requestJson('/challenge', { method: 'POST', body: JSON.stringify(body) });
    const walletSignature = await signWalletMessage(buildWalletDeviceRegistrationMessage({ ...body, challenge, publicKey: identity.signingPublicJwk }));
    await requestJson('/register', { method: 'POST', body: JSON.stringify({ ...body, challenge, walletSignature }) });
}
/** Gets a short-lived token using only a silent local P-256 signature. */
export async function getWalletDeviceSessionToken(walletAddress, identity, purpose = 'chat', origin = window.location.origin, forceRefresh = false) {
    assertPurpose(purpose);
    const canonical = canonicalOrigin(origin);
    const wallet = checkedWallet(walletAddress);
    if (identity.wallet !== wallet)
        throw new Error('Device identity wallet mismatch');
    const key = cacheKey(wallet, identity.deviceId, purpose, canonical);
    const cached = forceRefresh ? null : loadCached(key);
    if (cached && cached.wallet === wallet && cached.deviceId === identity.deviceId && cached.origin === canonical && cached.purpose === purpose) {
        return cached.token;
    }
    const body = challengeBody('session', wallet, identity, purpose, canonical);
    const { challenge } = await requestJson('/challenge', { method: 'POST', body: JSON.stringify(body) });
    const message = buildWalletDeviceSessionMessage({ ...body, challenge });
    const signature = signatureBase64Url(await signDeviceChallenge(identity, new TextEncoder().encode(message)));
    const result = await requestJson('/session', {
        method: 'POST',
        body: JSON.stringify({ ...body, challenge, signature }),
    });
    if (typeof result.token !== 'string' || !Number.isFinite(result.expiresIn) || result.expiresIn <= 0) {
        throw new Error('Invalid device session response');
    }
    const session = {
        token: result.token, wallet, deviceId: identity.deviceId, purpose, origin: canonical,
        expiresAt: Date.now() + Math.max(1, result.expiresIn - 15) * 1000,
    };
    saveCached(key, session);
    return session.token;
}
export function buildWalletDeviceListMessage(walletAddress, challenge, origin = window.location.origin) {
    return ['Dwinity wallet device owner action', `challenge: ${challenge}`, `wallet: ${checkedWallet(walletAddress)}`,
        'action: list', 'device: -', `origin: ${canonicalOrigin(origin)}`].join('\n');
}
export function buildWalletDeviceRevokeMessage(walletAddress, deviceId, challenge, origin = window.location.origin) {
    if (!/^[A-Za-z0-9._:-]{1,128}$/.test(deviceId))
        throw new Error('Invalid device id');
    return ['Dwinity wallet device owner action', `challenge: ${challenge}`, `wallet: ${checkedWallet(walletAddress)}`,
        'action: revoke', `device: ${deviceId}`, `origin: ${canonicalOrigin(origin)}`].join('\n');
}
export async function listWalletDevices(walletAddress, signWalletMessage, origin = window.location.origin) {
    const canonical = canonicalOrigin(origin);
    const wallet = checkedWallet(walletAddress);
    const challenge = await requestJson('/owner-challenge', {
        method: 'POST', body: JSON.stringify({ walletAddress: wallet, action: 'list', origin: canonical }),
    });
    const walletSignature = await signWalletMessage(buildWalletDeviceListMessage(wallet, challenge.challenge, canonical));
    const response = await requestJson('/list', {
        method: 'POST',
        body: JSON.stringify({ walletAddress: wallet, walletSignature, challenge: challenge.challenge, origin: canonical }),
    });
    return Array.isArray(response.devices) ? response.devices : [];
}
export async function revokeWalletDevice(walletAddress, deviceId, signWalletMessage, origin = window.location.origin) {
    if (!/^[A-Za-z0-9._:-]{1,128}$/.test(deviceId))
        throw new Error('Invalid device id');
    const canonical = canonicalOrigin(origin);
    const wallet = checkedWallet(walletAddress);
    const challenge = await requestJson('/owner-challenge', {
        method: 'POST', body: JSON.stringify({ walletAddress: wallet, action: 'revoke', deviceId, origin: canonical }),
    });
    const walletSignature = await signWalletMessage(buildWalletDeviceRevokeMessage(wallet, deviceId, challenge.challenge, canonical));
    await requestJson(`/${encodeURIComponent(deviceId)}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ walletAddress: wallet, walletSignature, challenge: challenge.challenge, origin: canonical }),
    });
}
